import { type Injector, runInInjectionContext } from '@angular/core';
import { ActivityEntityName, describeAlreadyLinked } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { ToastService } from '@workbuddy/ui/services';
import { taskCompletionRefusalDetail } from '@workbuddy/entities/work.task/task.completion.refusal';
import { embeddedItemCommandError } from '@workbuddy/surfaces/embedded-item';
import { resolveItemRecord } from '../row/item.row.record';
import { isPendingItemRow } from '../row/job.item.authorisation.group';
import { resolveContractorItemRowScope, type ContractorItemRowScope } from '../commands/contractor.item.row.commands';
import { SorTaskCountService } from '../services/item.task.count.service';

/**
 * What one line's write came to: done, left alone on purpose, or refused by the server. `alreadyLinked` counts the
 * attachments the server skipped because that SOR already held them; the run adds it up for the summary toast.
 */
export type ContractorBulkLineOutcome = { readonly kind: 'updated'; readonly note?: string; readonly alreadyLinked?: number } | { readonly kind: 'unchanged' | 'failed'; readonly reason: string; readonly alreadyLinked?: number };

export interface ContractorBulkLine extends ContractorItemRowScope {
  readonly pending: boolean;
}

export function resolveContractorBulkLines(rows: unknown[]): ContractorBulkLine[] {

  return (rows ?? []).flatMap(row => {
    const scope = resolveContractorItemRowScope(row);
    return scope ? [{ ...scope, pending: isPendingItemRow(resolveItemRecord(row) as { authorisation?: string } | null) }] : [];
  });
}

/** The words SOR Details shows for the same refusal, so a line reads alike on the page and in the bulk toast. */
function contractorBulkRefusal(error: unknown): string {

  return taskCompletionRefusalDetail(error) ?? embeddedItemCommandError(error).message;
}

export interface ContractorBulkReportOptions {
  /** The noun of the "not changed" toast — `SOR(s)` on the SORs tab; `item(s)` when absent. */
  readonly noun?: string;
}

/**
 * Runs the write once per line, one at a time, then refreshes and reports: one summary toast with the counts, as the
 * web bulk run does, and one "not changed" toast naming every line left alone or refused, one line per reason, so a
 * ticked line never drops out silently (Hung 2026-10-08, WBS-12406).
 */
export async function runContractorBulkLines(lines: ContractorBulkLine[], injector: Injector, write: (line: ContractorBulkLine) => Promise<ContractorBulkLineOutcome>, itemsChanged?: () => void, options: ContractorBulkReportOptions = {}): Promise<void> {

  const toastService = injector.get(ToastService);
  const taskCounts = injector.get(SorTaskCountService);
  // @ngneat/query provides its client through an injection token, not the QueryClient class.
  const queryClient = runInInjectionContext(injector, injectQueryClient);

  let updated = 0;
  let alreadyLinked = 0;
  const unchanged: LineReason[] = [];
  const failed: LineReason[] = [];
  const partly: LineReason[] = [];

  // Sequential: a line's task write can cascade onto the stage, and two deletes in flight would race on the stage document.
  for (const line of lines) {
    let outcome: ContractorBulkLineOutcome;
    try {
      outcome = await write(line);
    } catch (error: unknown) {
      outcome = { kind: 'failed', reason: contractorBulkRefusal(error) };
    }
    alreadyLinked += outcome.alreadyLinked ?? 0;

    if (outcome.kind === 'updated') {
      updated += 1;
      if (outcome.note) partly.push({ line, reason: outcome.note });
    }
    else (outcome.kind === 'unchanged' ? unchanged : failed).push({ line, reason: outcome.reason });
  }

  taskCounts.invalidate();
  await queryClient.invalidateQueries({ queryKey: [ActivityEntityName.JobItem, 'items'], refetchType: 'all' });
  itemsChanged?.();

  // The skip count is its own line, not a line `note`, so a repeat does not read as "partly changed".
  const counts = [`${updated} updated, ${unchanged.length} unchanged, ${failed.length} failed of ${lines.length}`, describeAlreadyLinked(alreadyLinked)]
    .filter(Boolean)
    .join('\n');
  if (failed.length) toastService.warning('Bulk action completed with failures', counts);
  else toastService.success('Bulk action completed', counts);

  const noun = options.noun ?? 'item(s)';
  const refused = [...unchanged, ...failed];
  if (refused.length) toastService.warning(`${refused.length} ${noun} not changed`, reportLines(refused));
  // A line that took part of the request is updated, but the part it left out is named, as the web run records it.
  if (partly.length) toastService.warning(`${partly.length} ${noun} partly changed`, reportLines(partly));
}

interface LineReason {
  readonly line: ContractorBulkLine;
  readonly reason: string;
}

/** One line per reason, naming every line it kept out: "{reason}: {line}, {line}." */
function reportLines(entries: LineReason[]): string {

  const groups = new Map<string, string[]>();
  for (const { line, reason } of entries) {
    // The server's sentences end with a full stop; the line gets one after its names instead.
    const key = reason.trim().replace(/\.+$/, '');
    groups.set(key, [...(groups.get(key) ?? []), line.name ?? line.itemId]);
  }

  return [...groups].map(([reason, names]) => `${reason}: ${names.join(', ')}.`).join('\n');
}
